<template>
  <!-- Article Detail Modal Overlay -->
  <Teleport to="body">
    <Transition name="modal-fade">
      <div 
        v-if="isOpen" 
        class="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto"
        @click.self="closeModal"
      >
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" @click="closeModal"></div>
        
        <!-- Modal Content -->
        <div class="relative w-full max-w-4xl mx-4 my-8 z-10">
          <Transition name="modal-slide">
            <div v-if="isOpen && article" class="bg-white rounded-2xl shadow-2xl overflow-hidden">
              
              <!-- Hero Image -->
              <div class="relative h-64 md:h-80 lg:h-96 overflow-hidden">
                <img 
                  :src="article.image?.replace('w=400&h=250', 'w=900&h=500')" 
                  :alt="article.imageAlt" 
                  class="w-full h-full object-cover"
                >
                <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                
                <!-- Close Button -->
                <button 
                  @click="closeModal" 
                  class="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/40 backdrop-blur-md text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                  aria-label="ปิด"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>

                <!-- Category Badge -->
                <div class="absolute top-4 left-4">
                  <span 
                    :class="categoryBadgeClass" 
                    class="px-4 py-1.5 rounded-full text-sm font-semibold backdrop-blur-md"
                  >
                    {{ article.category }}
                  </span>
                </div>
                
                <!-- Title on image -->
                <div class="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <h1 class="text-2xl md:text-3xl lg:text-4xl font-bold text-white drop-shadow-lg leading-tight">
                    {{ article.title }}
                  </h1>
                </div>
              </div>

              <!-- Article Meta -->
              <div class="px-6 md:px-8 py-5 border-b border-gray-100 bg-gray-50/50">
                <div class="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  <!-- Author -->
                  <div class="flex items-center gap-2">
                    <img 
                      v-if="article.authorAvatar" 
                      :src="article.authorAvatar" 
                      :alt="article.author"
                      class="w-8 h-8 rounded-full object-cover ring-2 ring-white shadow-sm"
                    >
                    <div v-else class="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <svg class="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"/>
                      </svg>
                    </div>
                    <span class="font-medium text-gray-800">{{ article.author || 'ทีมงาน Clustersie' }}</span>
                  </div>
                  
                  <!-- Divider -->
                  <span class="text-gray-300">|</span>
                  
                  <!-- Date -->
                  <div class="flex items-center gap-1.5">
                    <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                    <span>{{ article.date }}</span>
                  </div>
                  
                  <!-- Divider -->
                  <span class="text-gray-300">|</span>
                  
                  <!-- Read time -->
                  <div class="flex items-center gap-1.5">
                    <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <span>อ่าน {{ article.readTime || '5 นาที' }}</span>
                  </div>
                </div>

                <!-- Tags -->
                <div v-if="article.tags && article.tags.length" class="flex flex-wrap gap-2 mt-3">
                  <span 
                    v-for="tag in article.tags" 
                    :key="tag" 
                    class="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full hover:bg-gray-200 transition-colors cursor-default"
                  >
                    #{{ tag }}
                  </span>
                </div>
              </div>

              <!-- Article Content -->
              <div class="px-6 md:px-8 py-8">
                <div 
                  class="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-green-600 prose-strong:text-gray-900 prose-li:text-gray-700"
                  v-html="article.content"
                ></div>
              </div>

              <!-- Article Footer -->
              <div class="px-6 md:px-8 py-6 border-t border-gray-100 bg-gray-50/50">
                <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <!-- Share buttons -->
                  <div class="flex items-center gap-3">
                    <span class="text-sm text-gray-500 font-medium">แชร์บทความ:</span>
                    <button 
                      @click="shareArticle('facebook')"
                      class="w-9 h-9 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center transition-colors"
                      aria-label="แชร์ Facebook"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    </button>
                    <button 
                      @click="shareArticle('line')"
                      class="w-9 h-9 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center transition-colors"
                      aria-label="แชร์ LINE"
                    >
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/></svg>
                    </button>
                    <button 
                      @click="copyLink"
                      class="w-9 h-9 bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-full flex items-center justify-center transition-colors"
                      aria-label="คัดลอกลิงก์"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"/>
                      </svg>
                    </button>
                  </div>
                  
                  <!-- Close button -->
                  <button 
                    @click="closeModal" 
                    class="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium text-sm transition-colors"
                  >
                    ปิดบทความ
                  </button>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  article: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['close'])

const closeModal = () => {
  emit('close')
}

// Category badge color classes
const categoryBadgeClass = computed(() => {
  const colorMap = {
    green: 'bg-green-500/80 text-white',
    blue: 'bg-blue-500/80 text-white',
    purple: 'bg-purple-500/80 text-white',
    red: 'bg-red-500/80 text-white',
    orange: 'bg-orange-500/80 text-white',
    amber: 'bg-amber-500/80 text-white',
  }
  return colorMap[props.article?.categoryColor] || 'bg-green-500/80 text-white'
})

// Share article
const shareArticle = (platform) => {
  const url = window.location.href
  const title = props.article?.title || ''
  
  const shareUrls = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(title)}`,
    line: `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  }
  
  if (shareUrls[platform]) {
    window.open(shareUrls[platform], '_blank', 'width=600,height=400')
  }
}

// Copy link
const copyLink = async () => {
  try {
    await navigator.clipboard.writeText(window.location.href)
    alert('คัดลอกลิงก์แล้ว!')
  } catch {
    // Fallback
    const textArea = document.createElement('textarea')
    textArea.value = window.location.href
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
    alert('คัดลอกลิงก์แล้ว!')
  }
}

// Close on Escape key
if (typeof window !== 'undefined') {
  const handleKeydown = (e) => {
    if (e.key === 'Escape' && props.isOpen) {
      closeModal()
    }
  }
  
  if (typeof document !== 'undefined') {
    document.addEventListener('keydown', handleKeydown)
  }
}
</script>

<style scoped>
/* Modal fade transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

/* Modal slide transition */
.modal-slide-enter-active {
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-slide-leave-active {
  transition: all 0.25s ease-in;
}

.modal-slide-enter-from {
  opacity: 0;
  transform: translateY(30px) scale(0.97);
}

.modal-slide-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.98);
}

/* Prose styling override for v-html content */
.prose h3 {
  margin-top: 1.5em;
}

.prose ul {
  padding-left: 1.5em;
}

.prose p:last-child {
  margin-bottom: 0;
}
</style>
